import { AlertTriangle, AlertCircle, Info, X, ArrowRight } from 'lucide-react';
import type { BannerConfig, BannerSeverity } from '@/config/banners';

const severityIcon: Record<BannerSeverity, typeof AlertTriangle> = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const severityClasses: Record<BannerSeverity, string> = {
  critical: 'border-danger bg-danger-light text-danger',
  warning: 'border-warning bg-warning-light text-warning',
  info: 'border-border bg-card text-foreground',
};

export interface SystemBannerProps {
  banner: BannerConfig;
  onAction?: () => void;
  onDismiss?: () => void;
}

export function SystemBanner({
  banner,
  onAction,
  onDismiss,
}: SystemBannerProps) {
  const Icon = severityIcon[banner.severity];
  const dismissible = banner.dismiss !== 'none';

  return (
    <div
      data-severity={banner.severity}
      className={[
        'flex items-start gap-3 rounded-xl border p-4',
        severityClasses[banner.severity],
      ].join(' ')}
      role="status"
    >
      <Icon
        size={20}
        className="mt-0.5 shrink-0"
        aria-hidden="true"
      />

      <div className="flex flex-1 flex-col gap-1">
        <h3 className="text-sm font-semibold">{banner.copy.title}</h3>
        {banner.copy.description && (
          <p className="text-sm opacity-90">{banner.copy.description}</p>
        )}
        {onAction && banner.action && (
          <button
            type="button"
            onClick={onAction}
            className="mt-2 inline-flex items-center gap-1 self-start rounded-md bg-background px-3 py-1.5 text-sm font-semibold text-foreground border border-border hover:bg-muted"
          >
            {banner.action.label}
            <ArrowRight
              size={14}
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      {dismissible && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss ${banner.copy.title}`}
          className="shrink-0 rounded-md p-1 hover:bg-foreground/10"
        >
          <X
            size={16}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}
